/**
 * @fileoverview `useAllOrgKeyDates` — every event across the viewer's jobs in
 * an organisation, streamed in pages (MOR-2473).
 *
 * Walks `keyDates.queries.listForOrgPaginated` the way `useAllBankTransactions`
 * walks the bank ledger: a small first page paints the surface at once, then
 * larger pages stream in the background while `isLoadingAll` stays true. The
 * old single-array `listForOrg` query breaks past Convex's 8192 element return
 * cap once a user belongs to enough jobs; pages keep every response bounded,
 * and each page is its own subscription, so an edit to one job re-runs only
 * the page that holds it.
 *
 * The server pages over job memberships, so `numItems` counts jobs, not
 * events — see the query's doc comment for the sizing.
 *
 * Pass `orgId: undefined` to hold the query idle (no subscription is opened).
 */

import { useEffect, useMemo } from "react";
import { usePaginatedQuery } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { JobStatus } from "@cvx/jobs/schema";
import type { CalendarEventRow } from "./eventAdapter";

/** Jobs on the first page — small so the surface paints fast. */
export const INITIAL_JOBS_PER_PAGE = 20;
/**
 * Jobs per background page. At ~50–60 events per job this is ~4–5k events
 * and a few hundred KB per page: under the 8192 array cap with headroom for
 * jobs carrying many manual tasks.
 */
export const JOBS_PER_PAGE = 80;

export interface UseAllOrgKeyDatesArgs {
  /** The organisation to read; `undefined` keeps the query idle. */
  orgId: Id<"organizations"> | undefined;
  /** Narrow to these jobs (empty / undefined ⇒ every accessible job). */
  jobIds?: ReadonlyArray<Id<"jobs">>;
  /** Narrow to jobs holding one of these statuses (empty ⇒ all). */
  jobStatuses?: ReadonlyArray<JobStatus>;
  /** Narrow to events assigned to this user. */
  assigneeUserId?: Id<"users">;
}

export interface UseAllOrgKeyDatesResult {
  /** Every event loaded so far, sorted by date ascending. */
  events: CalendarEventRow[];
  /** True only before the first page arrives (or while idle). */
  isLoading: boolean;
  /** True while background pages are still streaming in. */
  isLoadingAll: boolean;
  loadedCount: number;
}

export function useAllOrgKeyDates({
  orgId,
  jobIds,
  jobStatuses,
  assigneeUserId,
}: UseAllOrgKeyDatesArgs): UseAllOrgKeyDatesResult {
  const { results, status, loadMore } = usePaginatedQuery(
    api.keyDates.queries.listForOrgPaginated,
    orgId
      ? {
          orgId,
          jobIds: jobIds && jobIds.length > 0 ? [...jobIds] : undefined,
          jobStatuses:
            jobStatuses && jobStatuses.length > 0
              ? [...jobStatuses]
              : undefined,
          assigneeUserId,
        }
      : "skip",
    { initialNumItems: INITIAL_JOBS_PER_PAGE },
  );

  useEffect(() => {
    if (status === "CanLoadMore") loadMore(JOBS_PER_PAGE);
  }, [status, loadMore]);

  const events = useMemo(
    () => [...results].sort((a, b) => a.date.localeCompare(b.date)),
    [results],
  );

  return {
    events,
    isLoading: status === "LoadingFirstPage",
    isLoadingAll: status !== "Exhausted",
    loadedCount: results.length,
  };
}
